import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as NewWindowIcon } from 'assets/new-window.svg';

import { DESIGN_REMIX_STATUS } from 'utlis/constants';

import GenerationOverlay from './GenerationOverlay';

import styles from './styles.module.scss';

const PORTRAIT_FALLBACK_W = 402;
const PORTRAIT_FALLBACK_H = 874;
const LANDSCAPE_FALLBACK_W = 874;
const LANDSCAPE_FALLBACK_H = 402;

const PrototypeSideBySidePanel = ({
  remixStatus,
  previewSrc,
  htmlUrl,
  processUuid,
  variantIndex,
  refinementPrompt,
  renderedDimensions,
  isLandscape,
}) => {
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  if (remixStatus == null) {
    return null;
  }

  const variantLabel = `Variant ${String.fromCharCode(65 + variantIndex)}`;

  const fallbackW = isLandscape ? LANDSCAPE_FALLBACK_W : PORTRAIT_FALLBACK_W;
  const fallbackH = isLandscape ? LANDSCAPE_FALLBACK_H : PORTRAIT_FALLBACK_H;
  const panelW = renderedDimensions?.width ?? fallbackW;
  const panelH = renderedDimensions?.height ?? fallbackH;
  const panelSizeStyle = { width: panelW, height: panelH, flexShrink: 0 };
  const panelFrameSizeStyle = { width: panelW + 32, height: panelH + 32, flexShrink: 0 };

  const isInProgress = remixStatus === DESIGN_REMIX_STATUS.IN_PROGRESS;
  const isCompleted = remixStatus === DESIGN_REMIX_STATUS.COMPLETED;

  const handleOpenPrototype = () => {
    if (!processUuid) {
      return;
    }

    const baseURL = `${process.env.REACT_APP_DESIGN_PRO_BASE_URL || window.location.origin}preview-share/`;

    window.open(baseURL + processUuid, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={cn(styles['prototype-side-panel'], isLandscape && styles['prototype-side-panel--landscape'])}>
      <div
        className={cn(
          styles['prototype-screen-wrapper'],
          isInProgress && styles['prototype-motion-enter'],
          isInProgress && isLandscape && styles['prototype-motion-enter--landscape']
        )}
        style={panelFrameSizeStyle}
      >
        <p className={cn(styles['screen-label'], styles['screen-label--prototype'])}>
          {variantLabel}
        </p>
        {isInProgress && (
          <GenerationOverlay width={panelW} height={panelH} />
        )}

        {isCompleted && previewSrc && (
          <div className={styles['prototype-image-wrapper']} style={panelSizeStyle}>
            <img
              src={previewSrc}
              alt={`${variantLabel} preview`}
              className={styles['prototype-image']}
              key={processUuid}
            />
            {refinementPrompt
              ? (
                <div className={styles['refinement-overlay']}>
                  <div className={styles['left']}>
                    <span className={styles['label']}>
                      Refinement
                    </span>
                    <button
                      type="button"
                      className={cn(
                        styles['text'],
                        !isPromptExpanded && styles['text--collapsed']
                      )}
                      onClick={() => setIsPromptExpanded(prev => !prev)}
                      title={!isPromptExpanded ? refinementPrompt : undefined}
                    >
                      {`"${refinementPrompt}"`}
                    </button>
                  </div>
                  {htmlUrl && (
                    <button
                      type="button"
                      className={styles['open-btn']}
                      onClick={handleOpenPrototype}
                      aria-label={`Open ${variantLabel} in new window`}
                    >
                      <NewWindowIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              )
              : (
                htmlUrl && (
                  <button
                    type="button"
                    className={styles['prototype-open-btn']}
                    onClick={handleOpenPrototype}
                    aria-label={`Open ${variantLabel} in new window`}
                  >
                    <NewWindowIcon className="w-5 h-5" />
                  </button>
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
};

PrototypeSideBySidePanel.propTypes = {
  remixStatus: PropTypes.number,
  previewSrc: PropTypes.string,
  htmlUrl: PropTypes.string,
  processUuid: PropTypes.string,
  variantIndex: PropTypes.number,
  refinementPrompt: PropTypes.string,
  renderedDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  isLandscape: PropTypes.bool,
};

PrototypeSideBySidePanel.defaultProps = {
  remixStatus: null,
  previewSrc: null,
  htmlUrl: null,
  processUuid: null,
  variantIndex: 0,
  refinementPrompt: null,
  renderedDimensions: null,
  isLandscape: false,
};

export default PrototypeSideBySidePanel;
